import { createSlice } from '@reduxjs/toolkit';
import { dispatch } from '../store';
// utils
import axios from '../../utils/axios';

// ----------------------------------------------------------------------

type InsiderTradingState = {
  isLoading: boolean;
  error: boolean;
  volume: any;
  ratio: any;
  insiderTradingActivities: any;
  insiderTradingActivitiesVolumes: any;
  insiderTradingActivitiesTrades: any;
  insiderTradingActivitiesHistoricalPrices: any;
  insiderCongressVolumes: any;
  insiderCongressTrades: any;
  insiderCongressHistoricalPrices: any;
  trendingCongressList: any;
};

const initialState: InsiderTradingState = {
  isLoading: false,
  error: false,
  volume: null,
  ratio: null,
  insiderTradingActivities: null,
  insiderTradingActivitiesVolumes: null,
  insiderTradingActivitiesTrades: null,
  insiderTradingActivitiesHistoricalPrices: null,
  insiderCongressVolumes: null,
  insiderCongressTrades: null,
  insiderCongressHistoricalPrices: null,
  trendingCongressList: null
};

const slice = createSlice({
  name: 'stockinsidertrading',
  initialState,
  reducers: {
    // START LOADING
    startLoading(state) {
      state.isLoading = true;
    },

    resetTradingActivities(state) {
      state.isLoading = false;
      state.insiderTradingActivities = null;
    },

    // HAS ERROR
    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },

    getSuccessVolume(state, action) {
      state.isLoading = false;
      state.volume = action.payload;
    },

    getSuccessRatio(state, action) {
      state.isLoading = false;
      state.ratio = action.payload;
    },
    saveInsiderTradingActivities(state, action) {
      state.isLoading = false;
      state.insiderTradingActivities = action.payload;
    },
    saveInsiderTradingActivitiesVolumes(state, action) {
      state.isLoading = false;
      state.insiderTradingActivitiesVolumes = action.payload.insiderTransactions;
      state.insiderCongressVolumes = action.payload.congressTransactions;
      state.insiderTradingActivitiesHistoricalPrices = action.payload.historicalPrices;
    },
    saveInsiderTradingActivitiesTrades(state, action) {
      state.isLoading = false;
      state.insiderTradingActivitiesTrades = action.payload.insiderTransactions;
      state.insiderCongressTrades = action.payload.congressTransactions;
      state.insiderCongressHistoricalPrices = action.payload.historicalPrices;
    },
    getTrendingCards(state, action) {
      state.isLoading = false;
      state.trendingCongressList = action.payload;
    },
    getInsiderTradingActivities(state, action) {
      state.isLoading = true;
    }
  }
});

// Reducer
export default slice.reducer;

// Actions
// Actions
export function resetTradingActivities() {
  return async () => {
    dispatch(slice.actions.resetTradingActivities());
  };
}
// ----------------------------------------------------------------------

export function getVolume(ticker: string) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get(`/api/v1/stocks/insidertrading/volume/${ticker}`);
      dispatch(slice.actions.getSuccessVolume(response?.data?.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getRatio(ticker: string) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get(`/api/v1/stocks/insidertrading/ratio/${ticker}`);
      dispatch(slice.actions.getSuccessRatio(response?.data?.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getInsiderTradingActivity(filters: { [index: string]: string | null | number }) {
  const queryParams = Object.keys(filters).map((item) => `${item}=${filters[item]}`);
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get(
        `/api/v1/insider-trading/activities?${queryParams.join('&')}`
      );
      dispatch(slice.actions.saveInsiderTradingActivities(response?.data));
    } catch (error) {
      dispatch(slice.actions.saveInsiderTradingActivities({ error: true }));
    }
  };
}

export function getInsiderTradingActivityVolumes(filters: {
  [index: string]: string | null | number;
}) {
  const queryParams = Object.keys(filters).map((item) => `${item}=${filters[item]}`);
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get(
        `/api/v1/insider-trading/activities/stockpageinsidercharts?${queryParams.join('&')}`
      );
      dispatch(
        slice.actions.saveInsiderTradingActivitiesVolumes({
          insiderTransactions:
            postProcessingAcitivitesData(response?.data?.insiderTransactions) ?? [],
          congressTransactions: response?.data?.congressTrades ?? [],
          historicalPrices: response?.data?.historicalPrices ?? []
        })
      );
    } catch (error) {
      dispatch(slice.actions.saveInsiderTradingActivitiesVolumes({ error: true }));
    }
  };
}

export function getInsiderTradingActivityTrades(filters: {
  [index: string]: string | null | number;
}) {
  const queryParams = Object.keys(filters).map((item) => `${item}=${filters[item]}`);
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get(
        `/api/v1/insider-trading/activities/stockpageinsidercharts?${queryParams.join('&')}`
      );
      dispatch(
        slice.actions.saveInsiderTradingActivitiesTrades({
          insiderTransactions:
            postProcessingAcitivitesData(response?.data?.insiderTransactions) ?? [],
          congressTransactions: response?.data?.congressTrades ?? [],
          historicalPrices: response?.data?.historicalPrices ?? []
        })
      );
    } catch (error) {
      dispatch(slice.actions.saveInsiderTradingActivitiesTrades({ error: true }));
    }
  };
}

export function getTrendingCongress() {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get(`/api/v1/cards/trending`);
      dispatch(slice.actions.getTrendingCards(response?.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

function postProcessingAcitivitesData(transactionsRaw: any) {
  // Post-processing in JavaScript:
  // Group raw data by yearMonth
  const monthGroups = {};
  transactionsRaw.forEach((tran: any) => {
    // @ts-ignore
    if (!monthGroups[tran.yearMonth]) {
      // @ts-ignore
      monthGroups[tran.yearMonth] = {};
    }
    // @ts-ignore
    monthGroups[tran.yearMonth][tran.transactionCode] = tran;
  });

  const filledResult = [];

  for (const [month, trans] of Object.entries(monthGroups)) {
    // @ts-ignore
    const purchase = trans.P;
    // @ts-ignore
    const sale = trans.S;
    if (purchase) {
      filledResult.push(purchase);
    } else if (sale) {
      // If purchase missing, copy avgPricePerShare from sale
      filledResult.push({
        yearMonth: month,
        transactionCode: 'P',
        avgPricePerShare: sale.avgPricePerShare,
        totalTransactions: 0,
        totalShares: 0,
        totalValue: 0
      });
    } else {
      // Unlikely but safe fallback if both missing
      filledResult.push({
        yearMonth: month,
        transactionCode: 'P',
        avgPricePerShare: 0,
        totalTransactions: 0,
        totalShares: 0,
        totalValue: 0
      });
    }

    if (sale) {
      filledResult.push(sale);
    } else if (purchase) {
      // If sale missing, copy avgPricePerShare from purchase
      filledResult.push({
        yearMonth: month,
        transactionCode: 'S',
        avgPricePerShare: purchase.avgPricePerShare,
        totalTransactions: 0,
        totalShares: 0,
        totalValue: 0
      });
    } else {
      // Unlikely but safe fallback if both missing
      filledResult.push({
        yearMonth: month,
        transactionCode: 'S',
        avgPricePerShare: 0,
        totalTransactions: 0,
        totalShares: 0,
        totalValue: 0
      });
    }
  }
  return filledResult;
}
